import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Container, Row, Col, Nav, Tab, Alert, Spinner, Modal, Button } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../../shared/context/AuthContext'
import FileUploadSection from './forms/FileUploadSection'
import DynamicFormSection from './DynamicFormSection'
import PrimaryContactForm from './contact/PrimaryContactForm'
import ReviewSubmissionForm from './forms/ReviewSubmissionForm'
import { submitDealFieldMappingService, GroupedFormFields, FormFieldMapping } from '../services/fieldMappingService'
import { AiParsingProgress } from '../../ai-pdf-parsing/components/AiParsingProgress'
import { useAiPdfParsing } from '../../ai-pdf-parsing/hooks/useAiPdfParsing'
import { AutoFillService } from '../../ai-pdf-parsing/services/autoFillService'
import { useFormValidation, createValidationConfig } from '../../validators'
import '../styles/SubmitDealApplication.css'

export interface ApplicationData {
  // File uploads (always required)
  applicationFiles: File[] // Changed to support multiple application files
  bankStatements: File[]
  
  // AI Parsing data (Phase 1 - stored but not used for auto-fill yet)
  parsedData?: any
  
  // Dynamic form fields - will be populated based on field mappings
  [key: string]: any
}

const getInitialData = (): ApplicationData => ({
  // File uploads
  applicationFiles: [],
  bankStatements: [],
})

const SubmitDealApplication: React.FC = () => {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<string>('files')
  const [applicationData, setApplicationData] = useState<ApplicationData>(getInitialData())
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Prevent default drag and drop behavior on the page to avoid accidental file opening
  useEffect(() => {
    const preventDefault = (e: DragEvent) => {
      e.preventDefault()
    }
    
    const preventDefaultDrop = (e: DragEvent) => {
      e.preventDefault()
      return false
    }

    document.addEventListener('dragover', preventDefault)
    document.addEventListener('drop', preventDefaultDrop)

    return () => {
      document.removeEventListener('dragover', preventDefault)
      document.removeEventListener('drop', preventDefaultDrop)
    }
  }, [])
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [submissionId, setSubmissionId] = useState<string | null>(null)
  
  // Dynamic form state
  const [fieldMappings, setFieldMappings] = useState<GroupedFormFields>({})
  const [categories, setCategories] = useState<string[]>([])
  const [isLoadingMappings, setIsLoadingMappings] = useState(true)
  const [mappingError, setMappingError] = useState<string | null>(null)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  
  // Validation state
  const [validationConfig, setValidationConfig] = useState({})
  const [sectionValidationState, setSectionValidationState] = useState<{ [category: string]: boolean }>({})
  const [completedSections, setCompletedSections] = useState<Set<string>>(new Set())
  
  // Initialize validation hook
  const {
    validationState,
    validateField,
    validateAllFields,
    hasValidationErrors,
    clearFieldValidation,
    setFieldValidation
  } = useFormValidation(validationConfig)

  // AI PDF Parsing integration
  const { parseFile, parseMultipleFiles, isLoading: isParsingPdf, parsedData, error: parsingError } = useAiPdfParsing()
  const [showParsingModal, setShowParsingModal] = useState(false)

  // Load field mappings on component mount
  useEffect(() => {
    loadFieldMappings()
  }, [])

  const loadFieldMappings = async () => {
    try {
      setIsLoadingMappings(true)
      setMappingError(null)
      
      const mappings = await submitDealFieldMappingService.getFormSchema()
      setFieldMappings(mappings)
      
      const categoryOrder = submitDealFieldMappingService.getCategoriesInOrder(mappings)
      setCategories(categoryOrder)
      
      // Initialize form data with default values for all fields
      const initialFormData = getInitialData()
      const allFields = Object.values(mappings).flat()
      
      allFields.forEach(field => {
        if (initialFormData[field.formFieldName] === undefined) {
          // Set default value based on field type
          switch (field.formFieldType) {
            case 'boolean':
              initialFormData[field.formFieldName] = false
              break
            case 'number':
            case 'currency':
            case 'percentage':
              initialFormData[field.formFieldName] = ''
              break
            default:
              initialFormData[field.formFieldName] = ''
          }
        }
      })
      setApplicationData(initialFormData)
      
      // Create validation configuration including additional owner fields
      console.log('🔧 Creating validation config for fields:', allFields.map(f => ({ 
        name: f.formFieldName, 
        type: f.formFieldType, 
        required: f.isRequired,
        validationRules: f.validationRules 
      })))
      
      const baseValidationConfig = createValidationConfig(allFields)
      
      console.log('🔧 Generated validation config:', baseValidationConfig)
      
      // Add validation configuration for additional owner fields
      const extendedValidationConfig = { ...baseValidationConfig }
      
      // For each Contact field, create validation rules for owner2_ and owner3_ prefixed versions
      allFields.forEach(field => {
        if (field.salesforceObject === 'Contact') {
          // Add owner2_ validation
          const owner2FieldName = `owner2_${field.formFieldName}`
          extendedValidationConfig[owner2FieldName] = {
            required: field.isRequired,
            validators: baseValidationConfig[field.formFieldName]?.validators || [],
            validateOnChange: true,
            validateOnBlur: true,
            formatOnInput: baseValidationConfig[field.formFieldName]?.formatOnInput || false,
            showSuccessIcon: true
          }
          
          // Add owner3_ validation
          const owner3FieldName = `owner3_${field.formFieldName}`
          extendedValidationConfig[owner3FieldName] = {
            required: field.isRequired,
            validators: baseValidationConfig[field.formFieldName]?.validators || [],
            validateOnChange: true,
            validateOnBlur: true,
            formatOnInput: baseValidationConfig[field.formFieldName]?.formatOnInput || false,
            showSuccessIcon: true
          }
        }
      })
      
      setValidationConfig(extendedValidationConfig)
      
      // Initialize section validation state
      const initialSectionState: { [category: string]: boolean } = {}
      categoryOrder.forEach(category => {
        initialSectionState[category] = false
      })
      setSectionValidationState(initialSectionState)
      
    } catch (error) {
      console.error('Error loading field mappings:', error)
      setMappingError('Failed to load form configuration. Please try refreshing the page.')
    } finally {
      setIsLoadingMappings(false)
    }
  }

  const handleLogout = async () => {
    await logout()
  }

  const handleGoHome = () => {
    navigate('/iso-dashboard')
  }

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false)
  }

  const handleBackToDashboard = () => {
    setShowSuccessModal(false)
    navigate('/iso-dashboard')
  }

  const handleSubmitAnother = () => {
    setShowSuccessModal(false)
    setSubmitSuccess(false)
    setSubmissionId(null)
    setApplicationData(getInitialData())
    setActiveTab('files')
    // Reset validation states
    setCompletedSections(new Set())
    setSectionValidationState({})
  }

  const updateApplicationData = (updates: Partial<ApplicationData>) => {
    setApplicationData(prev => ({ ...prev, ...updates }))
    
    // Clear errors for updated fields
    const updatedFields = Object.keys(updates)
    setErrors(prev => {
      const newErrors = { ...prev }
      updatedFields.forEach(field => {
        if (newErrors[field]) {
          delete newErrors[field]
        }
      })
      return newErrors
    })
  }

  const handleFieldChange = useCallback((fieldName: string, value: any) => {
    updateApplicationData({ [fieldName]: value })
    
    // Validate field on change
    const fieldValidation = validateField(fieldName, value)
    setFieldValidation(fieldName, {
      isValid: fieldValidation.isValid,
      message: fieldValidation.message,
      isValidated: true,
      isDirty: true
    })
    
    // Section validation will be updated automatically via useEffect
  }, [updateApplicationData, validateField, setFieldValidation])

  const handleSubmit = async () => {
    setIsSubmitting(true)
    setSubmitError(null)
    
    try {
      // Validate all required fields
      const allFields = Object.values(fieldMappings).flat()
      const validation = submitDealFieldMappingService.validateRequiredFields(applicationData, allFields)
      
      if (!validation.valid) {
        setSubmitError(`Missing required fields: ${validation.missingFields.join(', ')}`)
        setIsSubmitting(false)
        return
      }

      // Validate files
      if (!applicationData.applicationFiles || applicationData.applicationFiles.length === 0) {
        setSubmitError('At least one application file is required')
        setIsSubmitting(false)
        return
      }

      if (applicationData.applicationFiles.length > 3) {
        setSubmitError('Maximum 3 application files allowed')
        setIsSubmitting(false)
        return
      }

      if (!applicationData.bankStatements || applicationData.bankStatements.length < 3) {
        setSubmitError('At least 3 bank statement files are required')
        setIsSubmitting(false)
        return
      }

      // Submit to our enhanced backend API (which handles mapping internally)
      const formData = new FormData()
      
      // Add files
      applicationData.applicationFiles.forEach((file: File, index: number) => {
        formData.append('applicationFiles', file)
      })
      applicationData.bankStatements.forEach((file: File, index: number) => {
        formData.append('bankStatements', file)
      })
      
      // Add form data (excluding files)
      const { applicationFiles, bankStatements, ...formFields } = applicationData
      Object.entries(formFields).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          formData.append(key, String(value))
        }
      })
      
      console.log('Submitting application with dynamic fields:', {
        formData: formFields,
        files: {
          applicationFiles: applicationData.applicationFiles.map((f: File) => f.name),
          bankStatements: applicationData.bankStatements.map((f: File) => f.name)
        }
      })
      
      // Submit to our enhanced submission endpoint
      const response = await fetch('/api/iso-submit-deal/submit-application', {
        method: 'POST',
        credentials: 'include',
        body: formData
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Submission failed')
      }

      const result = await response.json()
      console.log('Submission result:', result)
      
      setSubmitSuccess(true)
      setSubmissionId(result.submissionId)
      setShowSuccessModal(true)
      
    } catch (error) {
      console.error('Error submitting application:', error)
      setSubmitError(error instanceof Error ? error.message : 'Application submission failed')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Memoized validation results to prevent re-render loops
  const sectionValidationResults = useMemo(() => {
    const results: { [category: string]: boolean } = {}
    
    // Files validation
    results['files'] = !!(applicationData.applicationFiles.length > 0 && applicationData.bankStatements.length >= 3)
    
    // Category validations - use validation state only, don't call validateField during render
    categories.forEach(category => {
      const categoryFields = fieldMappings[category] || []
      if (categoryFields.length === 0) {
        results[category] = true
        return
      }
      
      let isValid = true
      
      // Check validation state for primary fields
      categoryFields.forEach(field => {
        const fieldState = validationState[field.formFieldName]
        
        // Only consider field invalid if it has been validated and is invalid
        // For required fields, also check if they're empty and not validated yet
        if (fieldState?.isValidated && !fieldState.isValid) {
          isValid = false
        } else if (field.isRequired) {
          const value = applicationData[field.formFieldName] || ''
          if (!value.trim() && !fieldState?.isValidated) {
            isValid = false // Required field is empty and hasn't been validated yet
          }
        }
      })
      
      // For Primary Contact, also validate additional owners
      if (category === 'Primary Contact') {
        // Validate Owner 2 if exists
        if (applicationData.hasAdditionalOwner2) {
          categoryFields.forEach(field => {
            const fieldName = `owner2_${field.formFieldName}`
            const fieldState = validationState[fieldName]
            
            if (fieldState?.isValidated && !fieldState.isValid) {
              isValid = false
            } else if (field.isRequired) {
              const value = applicationData[fieldName] || ''
              if (!value.trim() && !fieldState?.isValidated) {
                isValid = false
              }
            }
          })
        }
        
        // Validate Owner 3 if exists  
        if (applicationData.hasAdditionalOwner3) {
          categoryFields.forEach(field => {
            const fieldName = `owner3_${field.formFieldName}`
            const fieldState = validationState[fieldName]
            
            if (fieldState?.isValidated && !fieldState.isValid) {
              isValid = false
            } else if (field.isRequired) {
              const value = applicationData[fieldName] || ''
              if (!value.trim() && !fieldState?.isValidated) {
                isValid = false
              }
            }
          })
        }
      }
      
      results[category] = isValid
    })
    
    // Review validation
    results['review'] = categories.every(cat => results[cat])
    
    return results
  }, [applicationData, fieldMappings, categories, validationState])
  
  // Non-mutating validation function for safe use during render
  const validateSection = useCallback((category: string): boolean => {
    return sectionValidationResults[category] || false
  }, [sectionValidationResults])
  
  const validateCurrentTab = (): boolean => {
    return validateSection(activeTab)
  }

  // Validate all fields in a specific tab and show errors immediately
  const validateAndShowTabErrors = useCallback((tabName: string) => {
    const currentFields = fieldMappings[tabName]
    if (!currentFields) return

    console.log(`🔍 Validating and showing errors for all fields in ${tabName} tab...`)
    
    for (const field of currentFields) {
      const fieldName = field.formFieldName
      const fieldValue = applicationData[fieldName] || ''
      const config = validationConfig[fieldName]
      
      if (config) {
        console.log(`🔍 Checking field: ${fieldName}`)
        console.log(`   Value: "${fieldValue}"`)
        console.log(`   Is required: ${config.required}`)
        console.log(`   Has validators: ${config.validators?.length || 0}`)
        
        const result = validateField(fieldName, fieldValue)
        console.log(`   Validation result:`, result)
        
        // Set validation state to show errors immediately
        setFieldValidation(fieldName, {
          isValid: result.isValid,
          message: result.message,
          isValidated: true,
          isDirty: true
        })
        
        if (!result.isValid) {
          console.log(`❌ Field ${fieldName} validation failed: ${result.message}`)
        } else {
          console.log(`✅ Field ${fieldName} validation passed`)
        }
      }
    }
  }, [fieldMappings, applicationData, validationConfig, validateField, setFieldValidation])

  const canProceedToNext = (): boolean => {
    return validateCurrentTab()
  }
  
  // Check if a section can be accessed (all previous sections must be completed)
  const canAccessSection = useCallback((targetSection: string): boolean => {
    if (targetSection === 'files') return true // File upload always accessible
    
    const allTabs = ['files', ...categories, 'review']
    const targetIndex = allTabs.indexOf(targetSection)
    
    if (targetIndex === -1) return false
    
    // Check if all previous sections are completed
    for (let i = 0; i < targetIndex; i++) {
      const section = allTabs[i]
      if (section === 'files') {
        if (!validateSection('files')) return false
      } else if (!sectionValidationState[section]) {
        return false
      }
    }
    
    return true
  }, [categories, validateSection, sectionValidationState])
  
  // Update section validation state with useEffect to prevent render loops
  useEffect(() => {
    const newState = { ...sectionValidationState }
    let hasChanges = false
    
    categories.forEach(category => {
      const isValid = sectionValidationResults[category] || false
      if (newState[category] !== isValid) {
        newState[category] = isValid
        hasChanges = true
        
        if (isValid) {
          setCompletedSections(prev => new Set(prev).add(category))
        } else {
          setCompletedSections(prev => {
            const newSet = new Set(prev)
            newSet.delete(category)
            return newSet
          })
        }
      }
    })
    
    if (hasChanges) {
      setSectionValidationState(newState)
    }
  }, [sectionValidationResults, categories, sectionValidationState])

  const getNextTab = (currentTab: string): string | null => {
    const tabs = ['files', ...categories, 'review']
    const currentIndex = tabs.indexOf(currentTab)
    return currentIndex < tabs.length - 1 ? tabs[currentIndex + 1] : null
  }

  const getPreviousTab = (currentTab: string): string | null => {
    const tabs = ['files', ...categories, 'review']
    const currentIndex = tabs.indexOf(currentTab)
    return currentIndex > 0 ? tabs[currentIndex - 1] : null
  }

  const handleNext = async () => {
    const nextTab = getNextTab(activeTab)
    
    // Validate current section before proceeding
    const currentSectionValid = validateCurrentTab()
    if (!nextTab || !currentSectionValid) {
      console.log('❌ Cannot proceed to next tab:', { 
        nextTab, 
        currentSectionValid, 
        activeTab,
        validationState: sectionValidationState 
      })
      
      // Validation errors will be shown through the existing validation state
      return
    }

    // Mark current section as completed
    if (activeTab !== 'files' && activeTab !== 'review') {
      setSectionValidationState(prev => ({
        ...prev,
        [activeTab]: true
      }))
      setCompletedSections(prev => new Set(prev).add(activeTab))
    }

    console.log('📝 Current tab:', activeTab, '-> Next tab:', nextTab)
    console.log('📄 Application files:', applicationData.applicationFiles.map(f => f.name))

    // If leaving files tab and have PDF(s), trigger AI parsing
    const pdfFiles = applicationData.applicationFiles.filter(file => file.type === 'application/pdf')
    if (activeTab === 'files' && pdfFiles.length > 0) {
      console.log('🚀 Triggering AI parsing for PDFs:', pdfFiles.map(f => f.name))
      setShowParsingModal(true)
      
      try {
        let result
        if (pdfFiles.length === 1) {
          console.log('🤖 Calling parseFile for single PDF...')
          result = await parseFile(pdfFiles[0])
        } else {
          console.log('🤖 Calling parseMultipleFiles for multiple PDFs...')
          result = await parseMultipleFiles(pdfFiles)
        }
        console.log('📊 Parse result:', result)
        
        if (result.success && result.data) {
          // Store parsed data
          updateApplicationData({ parsedData: result.data })
          console.log('✅ AI parsing completed successfully:', result.data)
          
          // Apply auto-fill to form fields
          console.log('🎯 Applying auto-fill to form...')
          const { updatedFormData, result: autoFillResult } = AutoFillService.applyParsedDataToForm(
            result.data, 
            applicationData
          )
          
          // Update form data with auto-filled values
          setApplicationData(updatedFormData)
          
          // Log auto-fill results
          console.log('📋 Auto-fill results:', autoFillResult)
          console.log('📊 Form data after auto-fill:', updatedFormData)
          
          // Trigger validation for all auto-filled fields
          console.log('🔍 Triggering validation for auto-filled fields...')
          
          // Use setTimeout to ensure the validation state updates are processed
          setTimeout(() => {
            autoFillResult.fieldsApplied.forEach(fieldName => {
              const fieldValue = updatedFormData[fieldName] || ''
              const fieldConfig = validationConfig[fieldName]
              
              console.log(`🔍 Validating field: ${fieldName}`)
              console.log(`   Value: "${fieldValue}"`)
              console.log(`   Config:`, fieldConfig)
              
              const validationResult = validateField(fieldName, fieldValue)
              console.log(`   Validation result:`, validationResult)
              
              setFieldValidation(fieldName, {
                isValid: validationResult.isValid,
                message: validationResult.message,
                isValidated: true,
                isDirty: true
              })
              
              if (!validationResult.isValid) {
                console.log(`❌ Validation error for ${fieldName}: ${validationResult.message}`)
              } else {
                console.log(`✅ Validation passed for ${fieldName}`)
              }
            })
          }, 100) // Small delay to ensure state updates are processed
          
          // Generate user feedback
          const report = AutoFillService.generateAutoFillReport(autoFillResult)
          console.log('📝 Auto-fill report:', report)
          
          // Validate auto-fill
          const validation = AutoFillService.validateAutoFillResults(autoFillResult)
          if (!validation.isValid) {
            console.warn('⚠️ Auto-fill validation warnings:', validation.warnings)
          }
          
          // TODO: Show user notification with auto-fill results
          // Could add a toast notification or alert showing how many fields were filled
          
        } else {
          console.warn('⚠️  AI parsing failed:', result.error)
          // Continue with normal flow - parsing failure doesn't prevent form submission
        }
      } catch (error) {
        console.error('❌ AI parsing error:', error)
        // Continue with normal flow - parsing is optional
      } finally {
        setShowParsingModal(false)
      }
    } else {
      console.log('ℹ️  Skipping AI parsing:', { 
        isFilesTab: activeTab === 'files', 
        hasPDF: pdfFiles.length > 0,
        fileTypes: applicationData.applicationFiles.map(f => f.type)
      })
    }

    // Navigate to next tab
    console.log('➡️  Navigating to:', nextTab)
    setActiveTab(nextTab)
  }

  const handlePrevious = () => {
    const previousTab = getPreviousTab(activeTab)
    if (previousTab) {
      setActiveTab(previousTab)
    }
  }
  
  // Handle clicking on sidebar navigation
  const handleTabClick = useCallback(async (targetTab: string) => {
    if (!canAccessSection(targetTab)) {
      console.log('❌ Cannot access section:', targetTab, 'Previous sections not completed')
      return
    }
    
    // Trigger AI parsing when leaving files tab with uploaded PDF (same as handleNext)
    const pdfFiles = applicationData.applicationFiles.filter(file => file.type === 'application/pdf')
    if (activeTab === 'files' && targetTab !== 'files' && pdfFiles.length > 0) {
      console.log('🚀 Triggering AI parsing for PDFs from sidebar navigation:', pdfFiles.map(f => f.name))
      setShowParsingModal(true)
      
      try {
        let result
        if (pdfFiles.length === 1) {
          console.log('🤖 Calling parseFile from sidebar navigation...')
          result = await parseFile(pdfFiles[0])
        } else {
          console.log('🤖 Calling parseMultipleFiles from sidebar navigation...')
          result = await parseMultipleFiles(pdfFiles)
        }
        console.log('📊 Parse result from sidebar:', result)
        
        if (result.success && result.data) {
          // Store parsed data
          updateApplicationData({ parsedData: result.data })
          console.log('✅ AI parsing completed successfully from sidebar:', result.data)
          
          // Apply auto-fill to form fields
          console.log('🎯 Applying auto-fill to form from sidebar...')
          const { updatedFormData, result: autoFillResult } = AutoFillService.applyParsedDataToForm(
            result.data, 
            applicationData
          )
          
          // Update form with auto-filled data
          updateApplicationData(updatedFormData)
          
          // Trigger validation for all auto-filled fields from sidebar
          console.log('🔍 Triggering validation for auto-filled fields from sidebar...')
          
          // Use setTimeout to ensure the validation state updates are processed
          setTimeout(() => {
            autoFillResult.fieldsApplied.forEach(fieldName => {
              const fieldValue = updatedFormData[fieldName] || ''
              const validationResult = validateField(fieldName, fieldValue)
              setFieldValidation(fieldName, {
                isValid: validationResult.isValid,
                message: validationResult.message,
                isValidated: true,
                isDirty: true
              })
              
              if (!validationResult.isValid) {
                console.log(`❌ Sidebar validation error for ${fieldName}: ${validationResult.message}`)
              } else {
                console.log(`✅ Sidebar validation passed for ${fieldName}`)
              }
            })
          }, 100) // Small delay to ensure state updates are processed
          
          console.log('🎉 Auto-fill report from sidebar:', autoFillResult)
        } else {
          console.log('⚠️ AI parsing completed but no data available from sidebar')
        }
      } catch (error) {
        console.error('❌ AI parsing failed during sidebar navigation:', error)
        // Continue with navigation even if AI parsing fails
      } finally {
        setShowParsingModal(false)
      }
    }
    
    // If trying to access a section that requires validation, validate current section first
    if (activeTab !== 'files' && activeTab !== 'review' && activeTab !== targetTab) {
      const currentValid = validateCurrentTab()
      if (currentValid) {
        setSectionValidationState(prev => ({
          ...prev,
          [activeTab]: true
        }))
        setCompletedSections(prev => new Set(prev).add(activeTab))
      }
    }
    
    setActiveTab(targetTab)
    
    // Validate and show errors for the new tab after a short delay
    setTimeout(() => {
      validateAndShowTabErrors(targetTab)
    }, 100)
  }, [canAccessSection, validateCurrentTab, activeTab, applicationData.applicationFiles, parseFile, parseMultipleFiles, updateApplicationData, setShowParsingModal, validateAndShowTabErrors])

  const renderTabContent = () => {
    if (activeTab === 'files') {
      return (
        <FileUploadSection
          applicationData={applicationData}
          updateApplicationData={updateApplicationData}
        />
      )
    }
    
    if (activeTab === 'review') {
      return (
        <ReviewSubmissionForm
          applicationData={applicationData}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
        />
      )
    }
    
    // Special handling for Primary Contact to show additional owners
    if (activeTab === 'Primary Contact') {
      const categoryFields = fieldMappings[activeTab]
      if (categoryFields && categoryFields.length > 0) {
        return (
          <PrimaryContactForm
            category={activeTab}
            fields={categoryFields}
            formData={applicationData}
            errors={Object.fromEntries(
              Object.entries(validationState).map(([key, state]) => [
                key, 
                state.isValidated && !state.isValid ? (state.message || 'Invalid') : ''
              ])
            )}
            onChange={handleFieldChange}
            validationState={validationState}
            onValidation={(fieldName, isValid, message) => {
              setFieldValidation(fieldName, {
                isValid,
                message,
                isValidated: true,
                isDirty: true
              })
            }}
          />
        )
      }
    }
    
    // Render dynamic form section for all other categories
    const categoryFields = fieldMappings[activeTab]
    if (categoryFields && categoryFields.length > 0) {
      return (
        <DynamicFormSection
          category={activeTab}
          fields={categoryFields}
          formData={applicationData}
          errors={Object.fromEntries(
            Object.entries(validationState).map(([key, state]) => [
              key, 
              state.isValidated && !state.isValid ? (state.message || 'Invalid') : ''
            ])
          )}
          onChange={handleFieldChange}
          validationState={validationState}
          onValidation={(fieldName, isValid, message) => {
            setFieldValidation(fieldName, {
              isValid,
              message,
              isValidated: true,
              isDirty: true
            })
          }}
        />
      )
    }
    
    return null
  }

  return (
    <>
    <div className="submit-deal-dashboard">
      {/* Left Sidebar Navigation */}
      <div className="submit-deal-sidebar ">
        <div className="submit-deal-sidebar-header">
          <div className="submit-deal-brand">
            
            <span className="submit-deal-brand-text">Submit Application</span>
          </div>
          
          {/* Home Button */}
          <button 
            className="submit-deal-home-btn"
            onClick={handleGoHome}
            title="Back to Dashboard"
          >
            <i className="bi bi-arrow-left me-2"></i>
            Back to Dashboard
          </button>
        </div>
        
        <nav className="submit-deal-sidebar-nav">
          <ul className="submit-deal-nav-list">
            {/* File Upload Tab - Always first */}
            <li className="submit-deal-nav-item">
              <button 
                className={`submit-deal-nav-button ${activeTab === 'files' ? 'active' : ''} ${validateSection('files') ? 'completed' : ''}`}
                onClick={() => handleTabClick('files')}
                type="button"
              >
                <i className="bi bi-cloud-upload submit-deal-nav-icon"></i>
                <span className="submit-deal-nav-text">File Uploads</span>
                {validateSection('files') && (
                  <i className="bi bi-check-circle-fill submit-deal-nav-check"></i>
                )}
              </button>
            </li>
            
            {/* Dynamic Category Tabs */}
            {categories.map((category) => {
              const getCategoryIcon = (cat: string): string => {
                switch (cat.toLowerCase()) {
                  case 'business':
                    return 'bi-building'
                  case 'business address':
                    return 'bi-geo-alt'
                  case 'primary contact':
                    return 'bi-person-circle'
                  case 'contact address':
                    return 'bi-house'
                  case 'deal information':
                    return 'bi-cash-coin'
                  case 'general':
                    return 'bi-info-circle'
                  default:
                    return 'bi-file-text'
                }
              }
              
              const canAccess = canAccessSection(category)
              const isCompleted = sectionValidationState[category]
              
              return (
                <li key={category} className="submit-deal-nav-item">
                  <button 
                    className={`submit-deal-nav-button ${activeTab === category ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${!canAccess ? 'disabled' : ''}`}
                    onClick={() => handleTabClick(category)}
                    type="button"
                    disabled={!canAccess}
                  >
                    <i className={`${getCategoryIcon(category)} submit-deal-nav-icon`}></i>
                    <span className="submit-deal-nav-text">{category}</span>
                    {isCompleted && (
                      <i className="bi bi-check-circle-fill submit-deal-nav-check"></i>
                    )}
                    {!canAccess && (
                      <i className="bi bi-lock-fill submit-deal-nav-lock"></i>
                    )}
                  </button>
                </li>
              )
            })}
            
            {/* Review Tab - Always last */}
            <li className="submit-deal-nav-item">
              <button 
                className={`submit-deal-nav-button ${activeTab === 'review' ? 'active' : ''} ${!canAccessSection('review') ? 'disabled' : ''}`}
                onClick={() => handleTabClick('review')}
                type="button"
                disabled={!canAccessSection('review')}
              >
                <i className="bi bi-check-circle submit-deal-nav-icon"></i>
                <span className="submit-deal-nav-text">Review & Submit</span>
                {!canAccessSection('review') && (
                  <i className="bi bi-lock-fill submit-deal-nav-lock"></i>
                )}
              </button>
            </li>
          </ul>
        </nav>

        <div className="submit-deal-sidebar-footer">
          <div className="submit-deal-user-info">
            <div className="submit-deal-user-name">{user?.name}</div>
            <div className="submit-deal-user-role">ISO Partner</div>
          </div>
          <button 
            className="submit-deal-logout-btn"
            onClick={handleLogout}
          >
            <i className="bi bi-box-arrow-right me-2"></i>
            Logout
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="submit-deal-main-content">
        <div className="submit-deal-content-wrapper">
          <div className="submit-deal-content-section">
            {/* Loading State */}
            {isLoadingMappings && (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <div className="mt-3">Loading form configuration...</div>
              </div>
            )}

            {/* Error State */}
            {mappingError && (
              <Alert variant="danger">
                <Alert.Heading>Configuration Error</Alert.Heading>
                <p>{mappingError}</p>
                <button 
                  className="btn btn-outline-danger"
                  onClick={loadFieldMappings}
                >
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Retry
                </button>
              </Alert>
            )}

            {/* Submit Error */}
            {submitError && (
              <Alert variant="danger" dismissible onClose={() => setSubmitError(null)}>
                <Alert.Heading>Submission Error</Alert.Heading>
                <p>{submitError}</p>
              </Alert>
            )}



            {/* Form Content */}
            {!isLoadingMappings && !mappingError && renderTabContent()}

            {/* Navigation Buttons */}
            {activeTab !== 'review' && (
              <div className="submit-deal-navigation">
                <div className="navigation-layout">
                  <div className="navigation-spacer">
                    {/* Invisible spacer to match business header width exactly */}
                  </div>
                  
                  <div className="submit-deal-nav-buttons">
                    <button 
                      className="btn btn-outline-secondary"
                      onClick={handlePrevious}
                      disabled={!getPreviousTab(activeTab)}
                    >
                     
                      Previous
                    </button>
                    
                    {getNextTab(activeTab) && (
                      <button 
                        className={`btn ${canProceedToNext() ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={handleNext}
                        disabled={!canProceedToNext()}
                      >
                        Next
                       
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>

    {/* Success Modal */}
    <Modal 
      show={showSuccessModal} 
      onHide={handleSuccessModalClose}
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header className="text-white text-center" style={{ backgroundColor: 'var(--color-primary)', borderBottom: 'none' }}>
        <Modal.Title className="text-white w-100 text-center">
          <i className="bi bi-check-circle-fill me-2"></i>
          Application Submitted Successfully!
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-center py-4">
        <div className="mb-3">
          <i className="bi bi-check-circle text-success" style={{ fontSize: '3rem' }}></i>
        </div>
        <h5 className="mb-3">Your application has been submitted successfully!</h5>
        {submissionId && (
          <div className="alert alert-info">
            <strong>Submission ID:</strong> {submissionId}
          </div>
        )}
        <p className="text-muted">
          Your application is now being processed. You will receive updates via email.
        </p>
      </Modal.Body>
      <Modal.Footer className="justify-content-center">
        <Button 
          variant="outline-primary" 
          onClick={handleBackToDashboard}
          className="me-2"
        >
          <i className="bi bi-house-door me-2"></i>
          Back to Dashboard
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSubmitAnother}
        >
          <i className="bi bi-plus-circle me-2"></i>
          Submit Another Deal
        </Button>
      </Modal.Footer>
    </Modal>

    {/* AI Parsing Progress Modal */}
    <AiParsingProgress
      isVisible={showParsingModal}
      fileName={applicationData.applicationFiles.length === 1 
        ? applicationData.applicationFiles[0]?.name 
        : `${applicationData.applicationFiles.length} application files`}
      onComplete={(result) => {
        console.log('AI Parsing completed:', result)
        setShowParsingModal(false)
      }}
      onError={(error) => {
        console.error('AI Parsing error:', error)
        setShowParsingModal(false)
      }}
      onCancel={() => {
        setShowParsingModal(false)
      }}
    />
    </>
  )
}

export default SubmitDealApplication
